import { useEffect, useRef, useState } from 'react'
import { Icon, PATH } from '../icons'
import {
  DRAFT_STATE_TEXT,
  EMPTY_DRAFT,
  OTHER_OPTION_LABEL,
  type AskInput,
  type QuestionDraft,
  type SubmittedAnswer,
  canSubmitDrafts,
  draftState,
  pickOption,
  skipDraft,
  toSubmittedAnswer,
  typeOther,
} from './ask-user'

// 提問面板（#1653）：在輸入框上方、一次一題。開場白不在這裡，畫在對話區。
// 單選點了就跳下一題；每題都已答或略過才能送出；✕ 整組不回答。

interface QuestionPanelProps {
  input: AskInput
  // 上次沒送成的草稿。送出失敗重讀後面板重掛，答過的不必再填一次
  initialDrafts?: QuestionDraft[]
  onDraftsChange?: (drafts: QuestionDraft[]) => void
  onSubmit: (answers: SubmittedAnswer[]) => void
  onDismiss: () => void
  // 關閉進行中：整個面板先停用，等略過 API 回來
  dismissing: boolean
}

export function QuestionPanel(props: QuestionPanelProps) {
  const { input, initialDrafts, onDraftsChange, onSubmit, onDismiss, dismissing } = props
  const { questions } = input
  const [index, setIndex] = useState(0)
  const [drafts, setDrafts] = useState<QuestionDraft[]>(initialDrafts ?? [])
  // 🔴 按下送出就鎖住。ref 擋同一批次的連點（state 要等重繪才生效），state 負責停用畫面
  const [submitted, setSubmitted] = useState(false)
  const submittedRef = useRef(false)
  const headingRef = useRef<HTMLParagraphElement>(null)
  // 只有使用者換頁才移焦點；剛出現時不能把焦點從輸入框搶走
  const navigatedRef = useRef(false)

  useEffect(() => {
    if (!navigatedRef.current) return
    navigatedRef.current = false
    headingRef.current?.focus()
  }, [index])

  const total = questions.length
  if (total === 0) return null
  const current = Math.min(index, total - 1)
  const question = questions[current]
  const draft = drafts[current] ?? EMPTY_DRAFT
  const locked = submitted || dismissing
  const ready = canSubmitDrafts(questions, drafts)

  const goTo = (next: number) => {
    if (next < 0 || next >= total || next === current) return
    navigatedRef.current = true
    setIndex(next)
  }

  // 改這一題；advance ＝ 改完自動跳下一題（最後一題不跳）
  const update = (next: QuestionDraft, advance = false) => {
    const copy = [...drafts]
    copy[current] = next
    setDrafts(copy)
    onDraftsChange?.(copy)
    if (advance) goTo(current + 1)
  }

  const submit = () => {
    if (!ready || locked || submittedRef.current) return
    submittedRef.current = true
    setSubmitted(true)
    onSubmit(questions.map((_, i) => toSubmittedAnswer(drafts[i] ?? EMPTY_DRAFT)))
  }

  const headingId = 'ask-panel-question'

  return (
    <section className="ask-panel" aria-labelledby={headingId}>
      <header className="ask-panel-head">
        <p className="ask-panel-question" id={headingId} ref={headingRef} tabIndex={-1}>
          {question.question}
        </p>
        <nav className="ask-pager" aria-label="切換題目">
          <button
            type="button" className="ask-icon-btn" aria-label="上一題"
            onClick={() => goTo(current - 1)} disabled={current === 0}
          >
            <Icon d={PATH.chevronLeft} size={16} />
          </button>
          <span className="ask-pager-count">{current + 1} of {total}</span>
          <button
            type="button" className="ask-icon-btn" aria-label="下一題"
            onClick={() => goTo(current + 1)} disabled={current === total - 1}
          >
            <Icon d={PATH.chevronRight} size={16} />
          </button>
        </nav>
        <button
          type="button" className="ask-icon-btn ask-close" aria-label="關閉，不回答這組提問"
          title="關閉，不回答這組提問" onClick={onDismiss} disabled={locked}
        >
          <Icon d={PATH.close} size={16} />
        </button>
      </header>

      {/* 每題一顆點：顏色與外框標出已答／略過／未處理，點了跳到那一題 */}
      <ol className="ask-steps" aria-label="每題狀態">
        {questions.map((q, i) => {
          const state = draftState(drafts[i] ?? EMPTY_DRAFT)
          return (
            <li key={i}>
              <button
                type="button" className={`ask-step state-${state}`}
                aria-label={`第 ${i + 1} 題：${DRAFT_STATE_TEXT[state]}`}
                aria-current={i === current ? 'step' : undefined}
                title={`${q.header || `第 ${i + 1} 題`}：${DRAFT_STATE_TEXT[state]}`}
                onClick={() => goTo(i)}
              />
            </li>
          )
        })}
      </ol>

      <fieldset className="ask-body" disabled={locked} aria-labelledby={headingId}>
        {question.options && (
          <ol className="ask-options">
            {question.options.map((option, i) => {
              const checked = draft.selected.includes(option.label)
              const text = (
                <span className="ask-option-text">
                  <span className="ask-option-label">{option.label}</span>
                  {option.description && (
                    <span className="ask-option-desc">{option.description}</span>
                  )}
                </span>
              )
              return (
                <li key={option.label}>
                  {question.multiSelect ? (
                    <label className={`ask-option ${checked ? 'on' : ''}`}>
                      <input
                        type="checkbox" checked={checked}
                        onChange={() => update(pickOption(question, draft, option.label))}
                      />
                      {text}
                    </label>
                  ) : (
                    // 單選用編號列：點了就選中並跳下一題
                    <button
                      type="button" className={`ask-option ${checked ? 'on' : ''}`}
                      aria-pressed={checked}
                      onClick={() => update(pickOption(question, draft, option.label), true)}
                    >
                      <span className="ask-option-num" aria-hidden="true">{i + 1}</span>
                      {text}
                    </button>
                  )}
                </li>
              )
            })}
          </ol>
        )}
        <label className={`ask-other ${draft.otherText.trim() ? 'on' : ''}`}>
          <span className="ask-other-icon" aria-hidden="true">
            <Icon d={PATH.pencil} size={16} />
          </span>
          <input
            type="text" value={draft.otherText} maxLength={500}
            placeholder={question.options ? OTHER_OPTION_LABEL : '輸入你的回答'}
            aria-label={question.options ? OTHER_OPTION_LABEL : '你的回答'}
            onChange={(e) => update(typeOther(question, draft, e.target.value))}
          />
        </label>
      </fieldset>

      <footer className="ask-panel-foot">
        <span className="ask-panel-note">
          {ready ? '送出後我會接著處理。' : '每一題作答或略過後才能送出。'}
        </span>
        <button
          type="button" className="ask-skip" disabled={locked}
          onClick={() => update(skipDraft(), true)}
        >
          略過
        </button>
        <button
          type="button" className="ask-submit" aria-label="送出答案" title="送出答案"
          onClick={submit} disabled={!ready || locked}
        >
          <Icon d={PATH.arrowUp} size={18} />
        </button>
      </footer>
    </section>
  )
}
